import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transactionAPI } from '../services/api';

interface CSVImportProps {
  onImportComplete: () => void;
}

const CSVImport: React.FC<CSVImportProps> = ({ onImportComplete }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    message: string;
    successCount: number;
    errorCount: number;
    errors: string[];
  } | null>(null);
  const [error, setError] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);

  const validateAndSetFile = async (selectedFile: File) => {
    if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setError('');
      setResult(null);
      await handleImport(selectedFile);
    } else {
      setError(t('csv:invalidFileType'));
      setFile(null);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const droppedFile = files[0];
      validateAndSetFile(droppedFile);
    }
  };

  const handleImport = async (fileToImport?: File) => {
    const targetFile = fileToImport || file;
    if (!targetFile) {
      setError(t('csv:fileRequired'));
      return;
    }

    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      const importResult = await transactionAPI.importCSV(targetFile);
      setResult(importResult);
      if (importResult.successCount > 0) {
        onImportComplete();
      }
    } catch (err: any) {
      console.error('CSV import error:', err);
      setError(err.response?.data?.error || t('csv:importError'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
    setError('');
    const fileInput = document.getElementById('csvFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div style={{ padding: '20px', marginBottom: '20px' }}>
      <h3>{t('csv:title')}</h3>
      
      <div style={{ marginBottom: '15px' }}>
        <div
          onDragOver={isLoading ? undefined : handleDragOver}
          onDragLeave={isLoading ? undefined : handleDragLeave}
          onDrop={isLoading ? undefined : handleDrop}
          style={{
            border: `2px dashed ${isLoading ? '#ccc' : (isDragOver ? '#007bff' : '#ddd')}`,
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: isLoading ? '#f5f5f5' : (isDragOver ? '#f8f9ff' : '#fafafa'),
            marginBottom: '15px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            opacity: isLoading ? 0.7 : 1
          }}
          onClick={isLoading ? undefined : () => document.getElementById('csvFile')?.click()}
        >
          <div style={{ marginBottom: '10px' }}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke={isDragOver ? '#007bff' : '#666'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ margin: '0 auto', display: 'block' }}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10,9 9,9 8,9" />
            </svg>
          </div>
          <p style={{ margin: '10px 0', fontSize: '16px', color: isDragOver ? '#007bff' : '#666' }}>
            {isLoading ? t('csv:importing') : (isDragOver ? t('csv:dropHere') : t('csv:dragDropText'))}
          </p>
          {!isLoading && (
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#999' }}>
              {t('csv:orClickToSelect')}
            </p>
          )}
        </div>
        
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{t('csv:formatInstructions')}</p>
          <code style={{ display: 'block', padding: '8px', backgroundColor: '#e9ecef', borderRadius: '3px', marginBottom: '10px' }}>
            {t('csv:formatHeader')}
          </code>
          
          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{t('csv:formatDescription')}</p>
          <ul style={{ margin: '0 0 10px 20px', padding: 0 }}>
            <li>{t('csv:typeField')}</li>
            <li>{t('csv:categoryField')}</li>
            <li>{t('csv:amountField')}</li>
            <li>{t('csv:descriptionField')}</li>
            <li>{t('csv:dateField')}</li>
            <li>{t('csv:settlementField')}</li>
          </ul>
          
          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{t('csv:exampleTitle')}</p>
          <code style={{ display: 'block', padding: '8px', backgroundColor: '#e9ecef', borderRadius: '3px', marginBottom: '10px' }}>
            {t('csv:exampleFormat')}
          </code>
          
          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{t('csv:notes')}</p>
          <ul style={{ margin: '0 0 5px 20px', padding: 0 }}>
            <li>{t('csv:note1')}</li>
            <li>{t('csv:note2')}</li>
            <li>{t('csv:note3')}</li>
          </ul>
        </div>
        
        <input
          id="csvFile"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        
        {(file || result || error) && (
          <button
            onClick={resetForm}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('csv:resetButton')}
          </button>
        )}
      </div>

      {file && (
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <p style={{ margin: 0, fontSize: '14px' }}>
            {t('csv:selectedFile')} <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
          </p>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {result && (
        <div style={{ padding: '15px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px' }}>
          <h4 style={{ margin: '0 0 10px 0' }}>{t('csv:importResult')}</h4>
          <p style={{ margin: '5px 0' }}>{t('csv:successCount')} {result.successCount} {t('common:count')}</p>
          <p style={{ margin: '5px 0' }}>{t('csv:errorCount')} {result.errorCount} {t('common:count')}</p>
          
          {result.errors.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ margin: '5px 0', fontWeight: 'bold' }}>{t('csv:errorDetails')}</p>
              <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {result.errors.map((error, index) => (
                  <li key={index} style={{ fontSize: '14px', color: '#721c24', marginBottom: '2px' }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CSVImport;